import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IconName } from 'src/app/helpers/icon-exports';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';

export type ToastState = 'Warning' | 'Info' | 'Error' | 'Success';

const ICON_MAP: Record<ToastState, IconName> = {
  Warning: 'alert',
  Info: 'info',
  Error: 'alert',
  Success: 'check',
};

const COLOR_MAP: Record<ToastState, string> = {
  Warning: '#f4b400',
  Info: '#3b82f6',
  Error: '#dc2626',
  Success: '#16a34a',
};

@Component({
    selector: 'app-validation-toast',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, SvgIconComponent],
    templateUrl: './validation-toast.component.html',
    styleUrl: './validation-toast.component.css'
})
export class ValidationToastComponent implements OnInit, OnChanges, OnDestroy {
  @Input() state: ToastState = 'Warning';
  @Input() inline: boolean = false;
  @Input() autoMessage: string = '';
  @Input() autoTitle: string = '';
  @Input() htmlMessage: string = '';
  @Input() persistent: boolean = false;
  @Input() actionText: string = '';
  @Input() closable: boolean = false;
  @Output() action = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  visible = false;
  validationMessage = '';
  validationTitle = '';
  displayTime = 5000;

  private dismissTimer: any;

  constructor(private cdr: ChangeDetectorRef, private sanitizer: DomSanitizer) {}

  get safeHtmlMessage(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.htmlMessage);
  }

  ngOnInit(): void {
    if (this.autoMessage) {
      this.validationMessage = this.autoMessage;
      this.visible = true;
    }
    if (this.autoTitle) {
      this.validationTitle = this.autoTitle;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['autoMessage']) {
      if (this.autoMessage) {
        this.validationMessage = this.autoMessage;
        this.visible = true;
      } else if (!this.htmlMessage) {
        this.visible = false;
      }
      this.cdr.markForCheck();
    }
    if (changes['autoTitle']) {
      this.validationTitle = this.autoTitle;
      this.cdr.markForCheck();
    }
    if (changes['htmlMessage']) {
      this.visible = !!this.htmlMessage;
      this.cdr.markForCheck();
    }
  }

  get iconName(): IconName { return ICON_MAP[this.state]; }
  get iconColor(): string { return COLOR_MAP[this.state]; }

  showToast(message: string, state: ToastState = 'Warning', title = ''): void {
    clearTimeout(this.dismissTimer);
    this.validationMessage = message;
    this.validationTitle = title;
    this.state = state;
    this.visible = true;
    this.cdr.markForCheck();
    if (!this.persistent) {
      this.dismissTimer = setTimeout(() => this.closeToast(), this.displayTime);
    }
  }

  closeToast(): void {
    this.visible = false;
    this.cdr.markForCheck();
    this.closed.emit();
  }

  ngOnDestroy(): void {
    clearTimeout(this.dismissTimer);
  }
}
